import {LitElement, html, css} from 'lit';
import apiService from '@utils/apiService';
import globalMediaQueryManager from '@utils/mediaQueryManager';
import {ifDefined} from 'lit/directives/if-defined.js';
import { unsafeHTML as litUnsafeHTML } from 'lit/directives/unsafe-html.js';
import {replaceLegacyFonts} from './replace-legacy-fonts.js';
import {styleMap} from 'lit/directives/style-map.js';

export class SoloLitElement extends LitElement {
  static get properties() {
    return {
      intersectionRatio: {
        type: Number,
      },
      lang: {
        type: String,
      },
      isMobile: {
        type: Boolean,
      },
      isTablet: {
        type: Boolean,
      },
      isLaptop: {
        type: Boolean,
      },
      isXLaptop: {
        type: Boolean,
      },
    };
  }

  static observerElement(element) {
    if (!element._observer) {
      element._observer = new IntersectionObserver(
          element.observerCallback, {threshold: element.intersectionRatio},
      );
    }
    element._observer.observe(element);
  }

  observerCallback(entries) {
    for (const entry of entries) {
      if (entry.intersectionRatio > this.intersectionRatio) {
        entry.target.displayCallback();
        entry.target._observer.unobserve(entry.target);
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.lang = localStorage.getItem('lang') || 'ka';
    if (this.displayCallback) {
      SoloLitElement.observerElement(this);
    }
    this._unsubscribeMedia = globalMediaQueryManager.subscribe((state) => {
      this._updateResponsiveProperties(state);
    });
    this._updateResponsiveProperties(globalMediaQueryManager.getCurrentState());
  }

  constructor() {
    super();
    this.intersectionRatio = 0.1;
    this.isMobile = false;
    this.isTablet = false;
    this.isLaptop = false;
    this.isXLaptop = false;
    this.observerCallback = this.observerCallback.bind(this);
    this.$ = new Proxy({}, {
      get: (obj, id) => this.shadowRoot.getElementById(id),
    },
    );
  }

  _updateResponsiveProperties(state) {
    if (this.isXLaptop !== state.isXLaptop) this.isXLaptop = state.isXLaptop;
    if (this.isLaptop !== state.isLaptop) this.isLaptop = state.isLaptop;
    if (this.isTablet !== state.isTablet) this.isTablet = state.isTablet;
    if (this.isMobile !== state.isMobile) this.isMobile = state.isMobile;
  }

  capitalize(str) {
    if (!str) return;
    str = str[0].toUpperCase() + str.slice(1);
    return str;
  }

  sendAnalyticsEventOld(eventName, eventLabel = '', eventCategory = '', isNonInteraction = true,
      sendToFB = false, customParams = {}) {
    if (!eventName) {
      return;
    }

    const params = {
      'event_category': eventCategory,
      'event_label': eventLabel,
      'non_interaction': isNonInteraction,
    };

    if (localStorage.getItem('loggedIn')) {
      params.client_key = JSON.parse(localStorage.getItem('userInfo'))?.clientKey;
    }

    if (Object.keys(customParams).length) {
      Object.assign(params, customParams);
    }

    if (window.gtag) {
      window.gtag('event', eventName, params);
    }

    if (sendToFB && window.fbq) {
      fbq('track', eventName, params);
    }
  }


  /**
     *
     * @param eventAction - Specific action of the client and the addressee of the action. Event action must start with
     * a letter and must be in lowercase. Use only letters, numbers, and underscores (Max 40 characters).
     * e.g. currency_choose, scool_pg_step_child_info, limits_selected.
     * @param eventLabel - The name of the product/sub-product, which is chosen by the customer. If we do not have event
     * label, we can pass empty string as a value. e.g. EURO / DOLLAR / GEL, bnpl / inst / amex, ''.
     * @param eventName - A collective name for a flow or process. Event names must start with a letter and must be in
     * uppercase. Use only letters, numbers, and underscores. Don't use spaces. e.g. OFFERS_HUB, NAVIGATION, LIMITS
     * @param isNonInteraction - Non-interaction events are the events which we would like to track, but are
     * not initiated
     * by the user on the website. Such as promotion banners. It gets boolean value.
     * @param sendToFB - It checks if the event should also send to Facebook as a meta event. It gets boolean value.
     * @param customParams - Other parameters can be sent in the customParams object. We can pass params
     * such as category,
     * segment, value (The monetary value of the event), currency (Currency of the items associated with the event, in
     * 3-letter ISO 4217 format. If you set value then currency is required for
     * revenue metrics to be computed accurately.
     * ). You can also create unique custom param as you need. e.g. {client_key: this.clientKey, channel:
     * this.soloRedirect ? 'solo' : 'bogge'}.
     */
  // eslint-disable-next-line max-len
  sendAnalyticsEvent(eventAction, eventLabel = '', eventName = '', isNonInteraction = true, sendToFB = false, customParams = {}) {
    if (!eventAction || !eventName || !window.fbq || !window.gtag) {
      return;
    }

    const params = {
      'event_action': eventAction,
      'event_label': eventLabel,
      'non_interaction': isNonInteraction,
    };

    if (localStorage.getItem('loggedIn')) {
      params.client_key = JSON.parse(localStorage.getItem('userInfo'))?.clientKey;
    }

    if (Object.keys(customParams).length) {
      Object.assign(params, customParams);
    }

    if (window.gtag) {
      window.gtag('event', eventName.toUpperCase(), params);
    }

    if (sendToFB && window.fbq) {
      fbq('track', eventAction, params);
    }
  }

  sendFacebookViewEvent(viewParams) {
    if (!window.fbq) {
      return;
    }

    if (!Object.keys(viewParams).length) {
      return;
    }

    fbq('track', 'ViewContent', viewParams);
  }

  dispatchHashChangeEvent() {
    this.updateComplete.then(() => {
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    });
  }
  apiCall(url, method = 'GET', data = {}, isText = false, contentType = 'application/json') {
    return apiService.execute({
      url,
      method,
      data,
      isText,
      contentType,
    });
  }

  dispatchCustomEvent(name, detail = null, bubbles = false, composed = false) {
    const event = new CustomEvent(name, {
      bubbles: bubbles,
      composed: composed,
      detail: detail,
    });
    this.dispatchEvent(event);
  }

  _scrollToTop() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  _scrollStickyFooter() {
    const to = this.shadowRoot.querySelector(`#${this.data.scrollTo}`).getBoundingClientRect().top;
    this.dispatchEvent(new CustomEvent('chat-button-up', {
      detail: {
        lower: false,
      },
      bubbles: true,
      composed: true,
    }));
    window.scrollTo({
      top: to - 100,
      behavior: 'smooth',
    });
  }

  addEndpoint(routeName) {
    let path = location.pathname;
    if (path.endsWith('/')) {
      path = path.substr(0, path.length - 1);
    }
    history.pushState(null, null, `${path}/${routeName}`);
  }
}
/**
 * Drop-in replacement for lit's `unsafeHTML` that normalizes legacy CMS font
 * families (MarkGEO / MarkGEOCAPS / BOG Headline) to the current `BOG` font
 * before rendering — caps families also get the uppercase treatment.
 * Every component imports `unsafeHTML` from here, so the rewrite is universal.
 * @param {*} value raw HTML string from CMS/html fields
 * @return {import('lit').DirectiveResult} lit directive result
 */
const unsafeHTML = (value) => litUnsafeHTML(replaceLegacyFonts(value));

export { html, css, unsafeHTML, ifDefined, styleMap };
